export type CheckStatus = 'pass' | 'warn' | 'fail' | 'info';

export interface RequirementCheck {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface ParsedBrowser {
  name: string;
  version: number | null;
  versionString: string;
  os: string;
  isMobile: boolean;
  isInternetExplorer: boolean;
  isLegacyEdge: boolean;
  userAgent: string;
}

export interface BrowserCheckResult {
  browser: ParsedBrowser;
  checks: RequirementCheck[];
  overall: 'pass' | 'warn' | 'fail';
}

const MIN_SUPPORTED: Record<string, number> = {
  Chrome: 100,
  Edge: 100,
  Firefox: 100,
  Safari: 16,
  Opera: 90,
};

const RECOMMENDED: Record<string, number> = {
  Chrome: 120,
  Edge: 120,
  Firefox: 120,
  Safari: 17,
};

const extractVersion = (ua: string, pattern: RegExp): { numeric: number | null; text: string } => {
  const match = ua.match(pattern);
  if (!match?.[1]) return { numeric: null, text: 'unknown' };
  const text = match[1];
  const numeric = parseFloat(text);
  return { numeric: Number.isFinite(numeric) ? numeric : null, text };
};

const parseOs = (ua: string): string => {
  if (/Windows NT 10\.0/.test(ua)) return 'Windows 10/11';
  if (/Windows NT 6\.3/.test(ua)) return 'Windows 8.1';
  if (/Windows NT 6\.1/.test(ua)) return 'Windows 7';
  if (/Windows/.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/.test(ua)) {
    const ios = ua.match(/OS (\d+)[._](\d+)/);
    return ios ? `iOS ${ios[1]}.${ios[2]}` : 'iOS';
  }
  if (/Mac OS X/.test(ua)) {
    const mac = ua.match(/Mac OS X (\d+)[._](\d+)/);
    return mac ? `macOS ${mac[1]}.${mac[2]}` : 'macOS';
  }
  if (/Android/.test(ua)) {
    const android = ua.match(/Android (\d+(?:\.\d+)?)/);
    return android ? `Android ${android[1]}` : 'Android';
  }
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
};

export const parseBrowser = (userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''): ParsedBrowser => {
  const ua = userAgent || '';
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const isInternetExplorer = /MSIE |Trident\//.test(ua);
  const isLegacyEdge = /Edge\//.test(ua) && !/Edg\//.test(ua);

  if (isInternetExplorer) {
    const version = extractVersion(ua, /(?:MSIE |rv:)(\d+(?:\.\d+)?)/);
    return {
      name: 'Internet Explorer',
      version: version.numeric,
      versionString: version.text,
      os: parseOs(ua),
      isMobile,
      isInternetExplorer: true,
      isLegacyEdge: false,
      userAgent: ua,
    };
  }

  if (isLegacyEdge) {
    const version = extractVersion(ua, /Edge\/(\d+(?:\.\d+)?)/);
    return {
      name: 'Edge (legacy)',
      version: version.numeric,
      versionString: version.text,
      os: parseOs(ua),
      isMobile,
      isInternetExplorer: false,
      isLegacyEdge: true,
      userAgent: ua,
    };
  }

  if (/Edg\//.test(ua)) {
    const version = extractVersion(ua, /Edg\/(\d+(?:\.\d+)?)/);
    return { name: 'Edge', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  if (/OPR\/|Opera/.test(ua)) {
    const version = extractVersion(ua, /(?:OPR|Opera)\/(\d+(?:\.\d+)?)/);
    return { name: 'Opera', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  if (/Firefox\/|FxiOS\//.test(ua)) {
    const version = extractVersion(ua, /(?:Firefox|FxiOS)\/(\d+(?:\.\d+)?)/);
    return { name: 'Firefox', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  if (/CriOS\//.test(ua)) {
    const version = extractVersion(ua, /CriOS\/(\d+(?:\.\d+)?)/);
    return { name: 'Chrome', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) {
    const version = extractVersion(ua, /Chrome\/(\d+(?:\.\d+)?)/);
    return { name: 'Chrome', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  if (/Safari\//.test(ua) && /Version\//.test(ua)) {
    const version = extractVersion(ua, /Version\/(\d+(?:\.\d+)?)/);
    return { name: 'Safari', version: version.numeric, versionString: version.text, os: parseOs(ua), isMobile, isInternetExplorer: false, isLegacyEdge: false, userAgent: ua };
  }

  return {
    name: 'Unknown',
    version: null,
    versionString: 'unknown',
    os: parseOs(ua),
    isMobile,
    isInternetExplorer: false,
    isLegacyEdge: false,
    userAgent: ua,
  };
};

const canUseStorage = (storage: Storage | undefined): boolean => {
  if (!storage) return false;
  try {
    const key = '__wohq_browser_check__';
    storage.setItem(key, '1');
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const cookiesEnabled = (): boolean => {
  try {
    document.cookie = 'wohq_browser_check=1; path=/; SameSite=Lax';
    const ok = document.cookie.includes('wohq_browser_check=');
    document.cookie = 'wohq_browser_check=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    return ok;
  } catch {
    return false;
  }
};

export const runBrowserRequirementChecks = (): BrowserCheckResult => {
  const browser = parseBrowser();
  const checks: RequirementCheck[] = [];

  if (browser.isInternetExplorer || browser.isLegacyEdge) {
    checks.push({
      id: 'browser',
      label: 'Supported browser',
      status: 'fail',
      detail: `${browser.name} is not supported. Use current Chrome, Edge, Firefox, or Safari.`,
    });
  } else if (browser.name === 'Unknown') {
    checks.push({
      id: 'browser',
      label: 'Supported browser',
      status: 'warn',
      detail: 'Could not identify this browser. Chrome, Edge, Firefox, or Safari is recommended.',
    });
  } else {
    const min = MIN_SUPPORTED[browser.name];
    const recommended = RECOMMENDED[browser.name];
    if (min && browser.version !== null && browser.version < min) {
      checks.push({
        id: 'browser',
        label: 'Supported browser',
        status: 'fail',
        detail: `${browser.name} ${browser.versionString} is too old. Please update to ${min}+ (recommended ${recommended}+).`,
      });
    } else if (recommended && browser.version !== null && browser.version < recommended) {
      checks.push({
        id: 'browser',
        label: 'Supported browser',
        status: 'warn',
        detail: `${browser.name} ${browser.versionString} should work, but ${recommended}+ is recommended.`,
      });
    } else {
      checks.push({
        id: 'browser',
        label: 'Supported browser',
        status: 'pass',
        detail: `${browser.name} ${browser.versionString} on ${browser.os} is supported.`,
      });
    }
  }

  checks.push({
    id: 'javascript',
    label: 'JavaScript',
    status: 'pass',
    detail: 'JavaScript is running. This app does not use or require Java.',
  });

  const cookieOk = cookiesEnabled();
  checks.push({
    id: 'cookies',
    label: 'Cookies',
    status: cookieOk ? 'pass' : 'fail',
    detail: cookieOk
      ? 'Cookies are enabled (needed to stay signed in).'
      : 'Cookies appear to be blocked. Sign-in and session features may fail.',
  });

  const localOk = canUseStorage(window.localStorage);
  checks.push({
    id: 'localStorage',
    label: 'Local storage',
    status: localOk ? 'pass' : 'fail',
    detail: localOk
      ? 'Browser storage is available for login and preferences.'
      : 'Local storage is blocked (private mode or a policy). Login may not persist.',
  });

  const fileApiOk = typeof File !== 'undefined' && typeof FileList !== 'undefined' && typeof FormData !== 'undefined';
  checks.push({
    id: 'fileApi',
    label: 'File uploads',
    status: fileApiOk ? 'pass' : 'fail',
    detail: fileApiOk
      ? 'This browser can attach and upload files. Use Choose Files if drag-and-drop is awkward.'
      : 'This browser cannot attach files.',
  });

  const dragDropOk = typeof DataTransfer !== 'undefined' && 'ondrop' in window;
  checks.push({
    id: 'dragDrop',
    label: 'Drag and drop',
    status: dragDropOk ? 'pass' : 'warn',
    detail: dragDropOk
      ? 'Drag-and-drop events are available. Drop files on the dashed upload box, not the rest of the page.'
      : 'Drag-and-drop may not work. Use the Choose Files button instead.',
  });

  const fetchOk = typeof fetch === 'function';
  checks.push({
    id: 'network',
    label: 'Modern network APIs',
    status: fetchOk ? 'pass' : 'fail',
    detail: fetchOk ? 'Fetch is available for loading data.' : 'This browser is missing required network APIs.',
  });

  const isSecure = window.isSecureContext;
  checks.push({
    id: 'https',
    label: 'Secure connection',
    status: isSecure ? 'pass' : 'warn',
    detail: isSecure
      ? 'This page is running in a secure context.'
      : 'This page is not HTTPS. Some features (uploads, clipboard) can be limited.',
  });

  checks.push({
    id: 'java',
    label: 'Java plugin',
    status: 'info',
    detail: 'Java is not required and is not used. Missing Java will not break this app.',
  });

  const overall = checks.some((check) => check.status === 'fail')
    ? 'fail'
    : checks.some((check) => check.status === 'warn')
      ? 'warn'
      : 'pass';

  return { browser, checks, overall };
};

export const buildBrowserCheckReport = (
  result: BrowserCheckResult,
  extras: { dropTest?: string; clickTest?: string } = {}
): string => {
  const lines = [
    'WorkOrder HQ browser check',
    `Checked: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `Result: ${result.overall.toUpperCase()}`,
    '',
    `Browser: ${result.browser.name} ${result.browser.versionString}`,
    `OS: ${result.browser.os}${result.browser.isMobile ? ' (mobile)' : ''}`,
    `Screen: ${window.screen.width}x${window.screen.height}`,
    `Viewport: ${window.innerWidth}x${window.innerHeight}`,
    `Language: ${navigator.language}`,
    `User agent: ${result.browser.userAgent}`,
    '',
    'Checks:',
    ...result.checks.map((check) => `- [${check.status.toUpperCase()}] ${check.label}: ${check.detail}`),
  ];

  if (extras.dropTest || extras.clickTest) {
    lines.push('', 'Interactive tests:');
    if (extras.dropTest) lines.push(`- Drag and drop: ${extras.dropTest}`);
    if (extras.clickTest) lines.push(`- Choose files: ${extras.clickTest}`);
  }

  return lines.join('\n');
};
